/**
 * DataSnapshotCover — decision-first title page. An oversized verdict word
 * leads, then a four-figure grid (Firm deliverable / Reserve need / LCOE at
 * gen / Ultimate COD), with the report title secondary and a dotted byline.
 *
 * NULL-SAFE (NO DATA LOSS): the verdict word comes from the canonical
 * `getDisplayVerdict(report)` (same source as the rest of the report), and
 * the four figures read the PERSISTED TRACK-B snapshots
 * (`report.firmCapacityVerdict`, `report.pathwayLcoeAtGen`) via
 * `resolveDataSnapshotFigures`. Legacy reports that pre-date those fields
 * render em-dash figures and a qualitative verdict word — never NaN, never
 * a throw.
 *
 * Theme-aware: the verdict word takes the warning/positive/negative theme
 * token matching the verdict tier; figures and rules read headingColor /
 * bodyTextColor / headingFont.
 */
import React from 'react';
import {
  resolveCoverTitle,
  resolveSiteWordmark,
  resolveBylineParts,
  resolveDataSnapshotFigures,
} from '../../../../lib/reportRendering/journalCoverData';
import { getDisplayVerdict } from '../../../../lib/reportVerdict';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { reportTypographySizes, reportHeadingInk } from '../../../../lib/reportSectionHeading';
import { COVER_MIN_HEIGHT_PX, COVER_METADATA_GRID_CLASS } from '../../../../lib/reportCoverTokens';
import type { CoverProps } from './types';
import { ReportPlatformCredit, platformCreditToneForTheme } from '../ReportPlatformCredit';

export const DataSnapshotCover: React.FC<CoverProps> = ({
  report, theme, branding,
  disclaimer,
}) => {
  const effectiveBranding = branding ?? {
    audienceMode: 'internal' as const,
    siteWordmark: resolveSiteWordmark(report, undefined),
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };

  const title = resolveCoverTitle(report);
  const wordmark = resolveSiteWordmark(report, branding);
  // COVER-CONTENT BATCH (1/2): the cover header is the SITE NAME, not the
  // "{ORG} · BTM Feasibility · {site}" kicker. The org identity is still
  // carried by the body verdict + byline.
  const kicker = wordmark;
  const byline = resolveBylineParts(report, branding).join(' · ');

  const verdict = getDisplayVerdict(report);
  // The verdict colour follows the tier through the theme tokens. A 'none'
  // tier (no score) renders the word in the heading colour so it never
  // collapses into invisible text.
  const verdictColor = roleColor(
    theme,
    verdict.tier === 'green' ? 'positive'
    : verdict.tier === 'amber' ? 'warning'
    : verdict.tier === 'red' ? 'negative'
    : 'ink',
  );
  const verdictWord = verdict.label && verdict.label !== '—' ? verdict.label : 'Assessment';

  const figures = resolveDataSnapshotFigures(report);

  return (
    <div
      className="relative flex flex-col justify-center pb-16"
      data-testid="cover-data-snapshot"
      data-cover-variant="data-snapshot"
      data-verdict-tier={verdict.tier}
      style={{ color: roleColor(theme, 'body'), minHeight: COVER_MIN_HEIGHT_PX }}
    >
      <div
        className="text-[11px] font-bold uppercase tracking-[0.26em] mb-4"
        style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont, opacity: 0.7 }}
        data-testid="cover-site-name-header"
      >
        {kicker}
      </div>

      {/* Oversized verdict word. */}
      <div
        className="leading-none"
        // Verdict display size keys off the resolved headingScale (editorial >
        // technical > compact) instead of a fixed clamp; the responsive band
        // is bounded by the resolved h2 size scaled up for the cover hero word.
        // Display font falls back to headingFont.
        style={{
          fontFamily: theme.displayFont || theme.headingFont,
          color: verdictColor,
          fontSize: `clamp(48px, 9vw, calc(${reportTypographySizes(theme).h2} * 4.33))`,
          fontWeight: 300,
          letterSpacing: '-0.03em',
        }}
        data-testid="cover-data-snapshot-verdict"
      >
        {verdictWord}
      </div>

      {/* Secondary title. */}
      <h1
        id="cover-title"
        className="mt-6 max-w-[30ch]"
        // Secondary title size keys off the resolved headingScale instead of a
        // fixed 24px; display font falls back to headingFont.
        style={{
          fontFamily: theme.displayFont || theme.headingFont,
          color: reportHeadingInk(theme),
          fontSize: reportTypographySizes(theme).h2,
          fontWeight: 400,
        }}
        data-testid="cover-data-snapshot-title"
      >
        {title}
      </h1>

      {/* Four-figure grid. */}
      <div
        className={`${COVER_METADATA_GRID_CLASS} mt-9`}
        style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}` }}
        data-testid="cover-data-snapshot-figures"
      >
        {figures.map((fig, idx) => (
          <div
            key={fig.id}
            className="pr-4 pt-4"
            style={{ borderRight: idx < figures.length - 1 ? `1px solid ${roleColorAlpha(theme, 'ink', 0.12)}` : undefined }}
            data-testid={`cover-data-snapshot-figure-${fig.id}`}
          >
            <div
              className="text-[9px] font-bold uppercase tracking-[0.16em]"
              style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont, opacity: 0.7 }}
            >
              {fig.label}
            </div>
            <div
              className="mt-1.5 leading-none tabular-nums"
              style={{ fontFamily: theme.headingFont, color: reportHeadingInk(theme), fontSize: 34 }}
            >
              {fig.value ?? '–'}
              {fig.value != null && fig.unit && (
                <span className="ml-1 text-[13px]" style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont }}>
                  {fig.unit}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {byline && (
        <div
          className="text-[11px] mt-8 uppercase tracking-[0.05em]"
          style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont, opacity: 0.85 }}
          data-testid="cover-data-snapshot-byline"
        >
          {byline}
        </div>
      )}

      {/* CONFIDENTIAL DISCLAIMER — bottom-right footnote (COVER-CONTENT BATCH 4),
          rendered at 80% of the prior 10px size (→ 8px). */}
      <div
        className="absolute bottom-0 right-0 text-right text-[8px] leading-relaxed max-w-md"
        style={{ color: roleColor(theme, 'body'), opacity: 0.8 }}
        data-testid="cover-data-snapshot-disclaimer"
      >
        {disclaimer}
      </div>

      {/* GATEWAY POWER platform credit — left-aligned just BELOW the cover box
          (absolute, top: 100% + 3px): free at every width (cover shell padding
          on screen, the rest of the page-tall #cover in print), zero height, and
          clear of the bottom-right disclaimer however it wraps. */}
      <ReportPlatformCredit
        tone={platformCreditToneForTheme(theme)}
        color={roleColor(theme, 'muted')}
        className="absolute left-0 top-[calc(100%_+_3px)]"
        testId="cover-data-snapshot-platform-credit"
      />
    </div>
  );
};
